import { useEffect, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { CustomTab } from '../../components/customTab';
import { Settlements } from './settlements';
import { useAuthHelper } from '../../utils/functions/authentication';
import { TPaymentsDashboardTab } from '../../types/paymentsDashboard';
import {
  AutoCompleteController,
  Header,
} from '../../components';
import { UploadReversals } from './common/uploadReversals';
import { useFormWithSchema } from '../../utils/functions/yupFormTypescript';
import * as yup from 'yup';
import { useGetAllEnterpriseQuery } from '../../services/common';
import './index.scss';
import { Transactions } from './transactions';

const TABS: TPaymentsDashboardTab[] = [
  {
    label: 'Settlements',
    tabIndex: '1',
    fragment: 'settlements',
  },
  {
    label: 'Transactions',
    tabIndex: '2',
    fragment: 'transactions',
  },
];

const formSchema = yup.object({
  enterprise: yup.number().required(),
});

export const PaymentsDashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const enterpriseId = searchParams.get('enterpriseId');
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormWithSchema(formSchema, {
    mode: 'onTouched',
    defaultValues: {
      enterprise: enterpriseId
        ? Number(enterpriseId)
        : undefined,
    },
  });
  const { data: listOfEnterprises } =
    useGetAllEnterpriseQuery([1]);
  const formattedListOfEnterprises = listOfEnterprises?.map(
    (ele) => ({
      value: ele.id,
      label: ele.companyName,
    })
  );

  const { checkIfRolePresent } = useAuthHelper();
  const hasUploadReversalAccess = checkIfRolePresent(
    'REVERSAL_UPLOAD'
  );

  const navigate = useNavigate();
  const { hash } = useLocation();
  const [activeTab, setActiveTab] = useState(
    TABS.find((ele) => `#${ele.fragment}` == hash) ||
      TABS[0]
  );

  const handleChange = (
    newSelectedTab: TPaymentsDashboardTab
  ) => {
    if (newSelectedTab) {
      setActiveTab(newSelectedTab);
      navigate(
        `/payout-dashboard#${newSelectedTab.fragment}`
      );
    }
  };

  useEffect(() => {
    if (listOfEnterprises?.length) {
      setValue('enterprise', listOfEnterprises[0].id);
    }
  }, [listOfEnterprises]);

  return (
    <div>
      <div className="flex justify-between my-2 pb-3 align-middle">
        <Header
          heading="Payments Dashboard"
          subHeading="View settlements and transactions here"
        />
        <div className="flex items-center -mt-4">
          {hasUploadReversalAccess && (
            <div className="mr-4">
              <UploadReversals />
            </div>
          )}
          <div className="w-60 enterprise-filter">
            <AutoCompleteController
              label="Enterprise"
              name="enterprise"
              control={control}
              options={formattedListOfEnterprises}
              error={errors.enterprise?.message}
            />
          </div>
        </div>
      </div>
      <CustomTab<
        TPaymentsDashboardTab,
        keyof TPaymentsDashboardTab
      >
        tabList={TABS}
        activeTab={activeTab}
        valueKey={'tabIndex'}
        labelKey={'label'}
        onTabChange={handleChange}
        tabPanel={(tab) => {
          switch (tab.tabIndex) {
            case '1':
              return (
                <Settlements
                  enterpriseId={watch('enterprise')}
                />
              );
            default:
              return (
                <Transactions
                  enterpriseId={watch('enterprise')}
                />
              );
          }
        }}
      />
    </div>
  );
};
